import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, CheckCircle, AlertCircle, Loader2, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function DirectInvestment() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [formData, setFormData] = useState({
    user_id: '',
    project_id: '',
    shares: '',
    investment_date: new Date().toISOString().split('T')[0],
    payment_status: 'bezahlt',
    profit_eligible: true,
    notes: ''
  });
  
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchProjects();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setErrorMessage('Fehler beim Laden der Benutzer');
    }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setErrorMessage('Fehler beim Laden der Projekte');
    }
  };

  const handleProjectChange = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    setSelectedProject(project);
    setFormData({ ...formData, project_id: projectId });
  };

  const handleUserChange = (userId) => {
    const user = users.find(u => u.id === userId);
    setSelectedUser(user);
    setFormData({ ...formData, user_id: userId });
  };

  const calculateTotal = () => {
    if (selectedProject && formData.shares) {
      return (parseFloat(formData.shares) * parseFloat(selectedProject.price_per_share)).toFixed(2);
    }
    return '0.00';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    // Validation
    if (!formData.user_id || !formData.project_id || !formData.shares || !formData.investment_date) {
      setErrorMessage('Bitte füllen Sie alle Pflichtfelder aus');
      setLoading(false);
      return;
    }

    if (parseInt(formData.shares) <= 0) {
      setErrorMessage('Anzahl der Anteile muss größer als 0 sein');
      setLoading(false);
      return;
    }

    if (selectedProject && parseInt(formData.shares) > selectedProject.available_shares) {
      setErrorMessage(`Nicht genügend Anteile verfügbar. Verfügbar: ${selectedProject.available_shares}`);
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/superadmin/investments/create-for-user`,
        {
          user_id: formData.user_id,
          project_id: formData.project_id,
          shares: parseInt(formData.shares),
          investment_date: formData.investment_date,
          payment_status: formData.payment_status,
          profit_eligible: formData.profit_eligible,
          notes: formData.notes || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccessMessage(`Investition erfolgreich erstellt! Investitionsnummer: ${response.data.investment.investment_number}`);
      
      // Reset form
      setFormData({
        user_id: '',
        project_id: '',
        shares: '',
        investment_date: new Date().toISOString().split('T')[0],
        payment_status: 'bezahlt',
        profit_eligible: true,
        notes: ''
      });
      setSelectedProject(null);
      setSelectedUser(null);
      
      // Refresh projects to get updated available shares
      fetchProjects();
    } catch (error) {
      console.error('Error creating investment:', error);
      setErrorMessage(error.response?.data?.detail || 'Fehler beim Erstellen der Investition');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/bess/dashboard')}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Zurück zum Dashboard
        </Button>
      </div>

      <Card className="shadow-xl border-2 border-blue-200">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-t-lg">
            <CardTitle className="text-2xl flex items-center">
              <DollarSign className="mr-2 h-6 w-6" />
              Direkte Investition erstellen
            </CardTitle>
            <CardDescription className="text-blue-50">
              SuperAdmin: Erstellen Sie Investitionen direkt für Benutzer
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {successMessage && (
              <Alert className="mb-6 bg-green-50 border-green-500">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  {successMessage}
                </AlertDescription>
              </Alert>
            )}

            {errorMessage && (
              <Alert className="mb-6 bg-red-50 border-red-500">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  {errorMessage}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* User Selection */}
              <div className="space-y-2">
                <Label htmlFor="user">Benutzer auswählen *</Label>
                <Select
                  value={formData.user_id}
                  onValueChange={handleUserChange}
                >
                  <SelectTrigger id="user">
                    <SelectValue placeholder="Wählen Sie einen Benutzer aus" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.username} ({user.email}) - {user.role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedUser && (
                  <p className="text-sm text-slate-600">
                    Ausgewählt: <strong>{selectedUser.username}</strong> ({selectedUser.email})
                  </p>
                )}
              </div>

              {/* Project Selection */}
              <div className="space-y-2">
                <Label htmlFor="project">Projekt auswählen *</Label>
                <Select
                  value={formData.project_id}
                  onValueChange={handleProjectChange}
                >
                  <SelectTrigger id="project">
                    <SelectValue placeholder="Wählen Sie ein Projekt aus" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.title} - {project.available_shares} Anteile verfügbar
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedProject && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-2">
                    <p className="text-sm text-slate-700">
                      <strong>Projekt:</strong> {selectedProject.title}
                    </p>
                    <p className="text-sm text-slate-700">
                      <strong>Standort:</strong> {selectedProject.location}
                    </p>
                    <p className="text-sm text-slate-700">
                      <strong>Preis pro Anteil:</strong> €{selectedProject.price_per_share?.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-sm text-slate-700">
                      <strong>Verfügbare Anteile:</strong> {selectedProject.available_shares} / {selectedProject.total_shares}
                    </p>
                  </div>
                )}
              </div>

              {/* Investment Date */}
              <div className="space-y-2">
                <Label htmlFor="investment_date">Investitionsdatum *</Label>
                <Input
                  id="investment_date"
                  type="date"
                  value={formData.investment_date}
                  onChange={(e) => setFormData({ ...formData, investment_date: e.target.value })}
                  required
                />
                <p className="text-xs text-slate-500">
                  Sie können ein beliebiges Datum wählen (auch in der Vergangenheit)
                </p>
              </div>

              {/* Shares */}
              <div className="space-y-2">
                <Label htmlFor="shares">Anzahl Anteile *</Label>
                <Input
                  id="shares"
                  type="number"
                  min="1"
                  max={selectedProject?.available_shares || 999999}
                  value={formData.shares}
                  onChange={(e) => setFormData({ ...formData, shares: e.target.value })}
                  placeholder="Anzahl der Anteile eingeben"
                  required
                />
                {selectedProject && formData.shares && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-lg font-semibold text-green-700">
                      Gesamtbetrag: €{calculateTotal()}
                    </p>
                  </div>
                )}
              </div>

              {/* Payment Status */}
              <div className="space-y-2">
                <Label htmlFor="payment_status">Zahlungsstatus</Label>
                <Select
                  value={formData.payment_status}
                  onValueChange={(value) => setFormData({ ...formData, payment_status: value })}
                >
                  <SelectTrigger id="payment_status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bezahlt">✅ Bezahlt</SelectItem>
                    <SelectItem value="teilweise_bezahlt">⏳ Teilweise bezahlt</SelectItem>
                    <SelectItem value="offen">⏳ Offen</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Profit Eligible */}
              <div className="space-y-2">
                <Label htmlFor="profit_eligible" className="flex items-center space-x-2">
                  <input
                    id="profit_eligible"
                    type="checkbox"
                    checked={formData.profit_eligible}
                    onChange={(e) => setFormData({ ...formData, profit_eligible: e.target.checked })}
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <span>Berechtigt</span>
                </Label>
                <p className="text-xs text-slate-500 ml-6">
                  Aktivieren Sie diese Option, wenn der Investor Ertraganteile erhalten soll
                </p>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notizen (optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Interne Notizen zur Investition..."
                  rows={3}
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/bess/dashboard')}
                  disabled={loading}
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Wird erstellt...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Investition erstellen
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
    </div>
  );
}

export default DirectInvestment;
